import { useRef } from 'react';

// Corner-swipe gestures on the orb screen: ↘ (down-right) and ↑ (up).
// Forgiving distance+angle detection, distinct from an orb tap (which barely
// moves). The caller owns the enable/disable policy (modals, demo, setting off)
// and what each direction does.
const SWIPE_MIN_DIST = 50; // px total travel to count as a deliberate swipe

const useSwipeGestures = ({ enabled, onSwipeDownRight, onSwipeUp }) => {
  const startRef = useRef(null);

  const onTouchStart = (e) => {
    if (!enabled) { startRef.current = null; return; }
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY };
  };

  // Acts on a recognised swipe; returns true once consumed.
  const tryTriggerSwipe = (dx, dy) => {
    if (Math.hypot(dx, dy) < SWIPE_MIN_DIST) return false; // ignore taps / tiny moves
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI; // 0 = right, 90 = down, -90 = up
    if (angle > 5 && angle < 85) { onSwipeDownRight(); return true; } // ↘ — most of the SE quadrant, forgiving of an imprecise diagonal
    if (angle < -40 && angle > -140) { onSwipeUp(); return true; } // ↑
    return false;
  };

  // Fire as soon as the gesture is recognised mid-move (more reliable on iOS,
  // where a moving touch can end as touchcancel rather than touchend).
  const onTouchMove = (e) => {
    const start = startRef.current;
    if (!start) return;
    const t = e.touches[0];
    if (tryTriggerSwipe(t.clientX - start.x, t.clientY - start.y)) {
      startRef.current = null; // consume so touchend doesn't re-trigger
    }
  };

  const onTouchEnd = (e) => {
    const start = startRef.current;
    startRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    tryTriggerSwipe(t.clientX - start.x, t.clientY - start.y);
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
};

export default useSwipeGestures;
